import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { Users, User, Plus, Trash2, ArrowLeft, Mail, Phone, MapPin, Send, Edit, KeyRound, Archive, RotateCcw, Search, BarChart3, Briefcase, X, Clock, CalendarCheck } from 'lucide-react';
import axios from 'axios';
import { TIMEZONE_OPTION_GROUPS } from '../utils/timezoneOptions';
import { tzRegion } from '../utils/timezone';
import TimezoneAuditDialog from '../components/TimezoneAuditDialog';
import Pagination from '../components/Pagination';
import { NameSweepBanner } from '../components/NameSweepBanner';
import { getOtThresholdHint } from '../utils/otThresholdHints';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

// Weekly OT threshold: free-typed while editing, clamped 1–120 (fallback 44) on blur/save.
const clampOtThreshold = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? Math.max(1, Math.min(120, n)) : 44;
};

// Grey helper line + one-click chip showing the typical statutory threshold for the worker's jurisdiction.
const OtThresholdHint = ({ country, state, current, onUse, testId }) => {
  const hint = getOtThresholdHint(country, state);
  if (!hint) return null;
  const isCurrent = parseFloat(current) === hint.hours;
  return (
    <p className="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap" data-testid={testId}>
      <span>Typical statutory threshold — {hint.label}: <strong>{hint.hours}h</strong>. Confirm with your jurisdiction.</span>
      {!isCurrent && (
        <button
          type="button"
          onClick={() => onUse(hint.hours)}
          className="px-2 py-0.5 rounded-full border border-blue-400/60 text-blue-300 hover:bg-blue-500/20 text-[11px]"
          data-testid={`${testId}-use-btn`}
        >
          Use {hint.hours}
        </button>
      )}
    </p>
  );
};

// Country-specific data
const COUNTRIES = [
  { value: 'US', label: 'United States', states: [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ]},
  { value: 'CA', label: 'Canada', states: [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
    'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
    'Quebec', 'Saskatchewan', 'Yukon'
  ]},
  { value: 'MX', label: 'Mexico', states: [
    'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas', 'Chihuahua',
    'Coahuila', 'Colima', 'Durango', 'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'México',
    'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro',
    'Quintana Roo', 'San Luis Potosí', 'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala',
    'Veracruz', 'Yucatán', 'Zacatecas'
  ]},
  { value: 'OTHER', label: 'Other', states: [] }
];

const ManageUsers = () => {
  const navigate = useNavigate();
  const { user, token, isAdmin, isSupervisor, loading } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [staffSearch, setStaffSearch] = useState('');
  const [payrollFilter, setPayrollFilter] = useState('all');
  const staffMatches = (u) => {
    const q = staffSearch.trim().toLowerCase();
    if (q && !((u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.role || '').toLowerCase().includes(q)
      || (u.user_type || '').toLowerCase().includes(q))) return false;
    if (payrollFilter === 'on' && u.qb_payroll_exempt) return false;
    if (payrollFilter === 'off' && !u.qb_payroll_exempt) return false;
    return true;
  };
  const [pageSize, setPageSize] = useState(() => {
    try { return Number(localStorage.getItem('ht_users_page_size')) || 10; } catch { return 10; }
  });
  const [page, setPage] = useState(1);
  const [archivePage, setArchivePage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // {user_id: {active, in_progress, completed}} — fetched once per page load.
  // Powers both the inline "active jobs" badge on each row and the seed for
  // the Stats modal (which fetches the full breakdown on open).
  const [jobStats, setJobStats] = useState({});
  const [statsUser, setStatsUser] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showBulkArchiveDialog, setShowBulkArchiveDialog] = useState(false);
  const [showBulkOtDialog, setShowBulkOtDialog] = useState(false);
  const [bulkOt, setBulkOt] = useState({ overtime_policy: 'averaging', overtime_weekly_threshold: 44, averaging_agreement_date: '' });
  const [otDefaults, setOtDefaults] = useState({ overtime_policy_default: 'standard', overtime_weekly_threshold_default: 44 });
  const [bulkOtSaving, setBulkOtSaving] = useState(false);
  const [bulkArchiveReason, setBulkArchiveReason] = useState('');
  const [bulkArchiving, setBulkArchiving] = useState(false);
  const [teams, setTeams] = useState([]);
  const [editProjects, setEditProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showTzAudit, setShowTzAudit] = useState(false);
  // Bulk hourly-rate editor (Jun 2026 — margins)
  const [showRatesDialog, setShowRatesDialog] = useState(false);
  const [rateRows, setRateRows] = useState([]);
  const [ratesSaving, setRatesSaving] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [editCountry, setEditCountry] = useState('US');
  const [editData, setEditData] = useState({
    name: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone: '',
    street_address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    role: 'user',
    user_type: 'employee',
    shift_duration: 8.0,
    timezone: 'America/Toronto',
    team_id: '',
    role_change_reason: ''
  });
  // Role history (Phase 2 — Jun 2026): per-user audit trail of every
  // role change. Loaded lazily when the edit dialog opens.
  const [roleHistory, setRoleHistory] = useState([]);
  const [roleHistoryLoading, setRoleHistoryLoading] = useState(false);
  const [showRoleHistory, setShowRoleHistory] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone: '',
    street_address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    role: 'user',
    user_type: 'employee',
    password: 'temp',
    shift_duration: 8.0,
    timezone: 'America/Toronto',
    break_policy: '',
    qb_payroll_exempt: false,
    overtime_policy: 'standard',
    overtime_weekly_threshold: 44,
    averaging_agreement_date: ''
  });

  const currentCountryData = COUNTRIES.find(c => c.value === selectedCountry);
  const stateLabel = selectedCountry === 'US' ? 'State' : selectedCountry === 'CA' ? 'Province' : 'State/Province';
  const postalCodeLabel = selectedCountry === 'US' ? 'ZIP Code' : 'Postal Code';

  useEffect(() => {
    // Wait for auth to finish loading before checking admin status
    if (loading) {
      return;
    }
    
    // Check authentication first - redirect to signin if not logged in
    if (!token || !user) {
      navigate('/signin');
      return;
    }
    
    // Then check admin/supervisor status - redirect to home if neither
    if (!isAdmin && !isSupervisor) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators and supervisors can manage users',
        variant: 'destructive'
      });
      navigate('/home');
      return;
    }
    
    // Finally, fetch data if authenticated and authorized
    fetchUsers();
    fetchTeams();
    fetchJobStats();
    fetchOtDefaults();
  }, [token, user, isAdmin, isSupervisor, loading, navigate]);

  const fetchOtDefaults = async () => {
    try {
      const res = await axios.get(`${API}/company/overtime-defaults`, { headers: { Authorization: `Bearer ${token}` } });
      const d = { overtime_policy_default: res.data.overtime_policy_default || 'standard', overtime_weekly_threshold_default: res.data.overtime_weekly_threshold_default ?? 44 };
      setOtDefaults(d);
      setFormData((f) => ({ ...f, overtime_policy: d.overtime_policy_default, overtime_weekly_threshold: d.overtime_weekly_threshold_default }));
      setBulkOt((b) => ({ ...b, overtime_policy: d.overtime_policy_default, overtime_weekly_threshold: d.overtime_weekly_threshold_default }));
    } catch { /* keep built-in defaults */ }
  };

  const fetchJobStats = async () => {
    try {
      const res = await axios.get(`${API}/users/job-stats-bulk`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJobStats(res.data || {});
    } catch (_e) {
      // Non-fatal — badges just won't render
    }
  };

  const openStatsModal = async (u) => {
    setStatsUser(u);
    setStatsData(null);
    setStatsLoading(true);
    try {
      const res = await axios.get(`${API}/users/${u.id}/job-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatsData(res.data);
    } catch (err) {
      console.error('Stats fetch failed', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const openRatesDialog = async () => {
    try {
      const { data } = await axios.get(`${API}/users/rates`, { headers: { Authorization: `Bearer ${token}` } });
      setRateRows((data.users || []).map((u) => ({ ...u, hourly_rate: u.hourly_rate ?? '' })));
      setShowRatesDialog(true);
    } catch (e) {
      toast({ title: 'Could not load rates', description: e.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    }
  };

  const saveRates = async () => {
    setRatesSaving(true);
    try {
      const rates = rateRows.map((r) => ({ user_id: r.id, hourly_rate: r.hourly_rate === '' ? null : parseFloat(r.hourly_rate) }));
      const { data } = await axios.put(`${API}/users/bulk-rates`, { rates }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Hourly rates saved', description: `${data.updated} worker(s) updated. Job Margins now uses these labor costs.` });
      setShowRatesDialog(false);
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    } finally { setRatesSaving(false); }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { scope: 'all' }
      });

      // Defensive normalization. Any user row with missing/nullable string
      // fields gets safe defaults BEFORE React renders it, so a single bad
      // record (legacy data, in-flight create, partial migration) can never
      // crash the page with "Cannot read properties of null".
      const normalize = (u) => ({
        ...u,
        id: u?.id || '',
        name: u?.name ?? '',
        first_name: u?.first_name ?? '',
        last_name: u?.last_name ?? '',
        email: u?.email ?? '',
        phone: u?.phone ?? '',
        address: u?.address ?? '',
        role: u?.role ?? 'user',
        user_type: u?.user_type ?? 'employee',
      });
      const rows = (response.data || []).map(normalize);

      // Sort users alphabetically by first name.
      const safeFirst = (u) => {
        if (u.first_name) return String(u.first_name).toLowerCase();
        if (u.name)       return String(u.name).split(' ')[0].toLowerCase();
        if (u.email)      return String(u.email).toLowerCase();
        return '';
      };
      rows.sort((a, b) => safeFirst(a).localeCompare(safeFirst(b)));

      setUsers(rows);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  // Persist page size + reset page when underlying data changes
  useEffect(() => {
    try { localStorage.setItem('ht_users_page_size', String(pageSize)); } catch { /* ignore */ }
  }, [pageSize]);
  useEffect(() => { setPage(1); }, [staffSearch, payrollFilter, pageSize, users.length]);
  useEffect(() => { setArchivePage(1); }, [archivedUsers.length, pageSize]);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
      const pr = await axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } });
      setEditProjects(Array.isArray(pr.data) ? pr.data.filter((p) => p.status !== 'closed') : []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    setFormData({
      ...formData,
      country: value,
      state: ''
    });
  };

  const handleStateChange = (value) => {
    setFormData({
      ...formData,
      state: value
    });
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Build full name from first and last name
      const userData = {
        ...formData,
        name: [formData.first_name, formData.middle_name, formData.last_name].map((x) => (x || '').trim()).filter(Boolean).join(' '),
        overtime_weekly_threshold: clampOtThreshold(formData.overtime_weekly_threshold),
        averaging_agreement_date: formData.overtime_policy === 'averaging' && formData.averaging_agreement_date ? formData.averaging_agreement_date : null,
      };
      if (!userData.break_policy) delete userData.break_policy;
      
      await axios.post(`${API}/users/create`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Success',
        description: 'User added successfully! Invitation email and SMS sent.',
      });
      
      setShowAddDialog(false);
      setFormData({
        first_name: '',
    middle_name: '',
        last_name: '',
        email: '',
        phone: '',
        street_address: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'US',
        role: 'user',
        user_type: 'employee',
        password: 'temp',
        shift_duration: 8.0,
        timezone: 'America/Toronto',
        break_policy: '',
        qb_payroll_exempt: false,
        overtime_policy: otDefaults.overtime_policy_default,
        overtime_weekly_threshold: otDefaults.overtime_weekly_threshold_default,
        averaging_agreement_date: ''
      });
      setSelectedCountry('US');
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Failed to Add User',
        description: error.response?.data?.detail || error.response?.data?.message || 'An error occurred while adding the user. Please try again or contact support.',
        variant: 'destructive',
        duration: 8000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    const reason = window.prompt(
      `Archive ${userName || 'this user'}?\n\nThis user will be moved to the archive (kept for 7 years) and can be restored at any time.\n\nOptional: enter a reason (e.g., "Left company", "Terminated").`
    );
    if (reason === null) return;  // user cancelled
    
    setIsLoading(true);
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: reason ? { archive_reason: reason } : undefined,
      });
      
      toast({
        title: 'User archived',
        description: 'The user has been moved to the archive. Use the Archive tab to restore.',
      });
      
      fetchUsers();
      if (showArchive) fetchArchivedUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || error.response?.data?.message || 'Failed to archive user',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchArchivedUsers = async () => {
    try {
      setLoadingArchive(true);
      const res = await axios.get(`${API}/users/archived`, { headers: { Authorization: `Bearer ${token}` } });
      setArchivedUsers(res.data || []);
    } catch (error) {
      console.error('Failed to fetch archived users', error);
    } finally {
      setLoadingArchive(false);
    }
  };

  const handleRestoreUser = async (userId, userName) => {
    if (!window.confirm(`Restore ${userName} to active staff?`)) return;
    try {
      await axios.post(`${API}/users/${userId}/restore`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'User restored', description: `${userName} is back in your active staff list.` });
      fetchArchivedUsers();
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to restore user',
        variant: 'destructive',
      });
    }
  };

  // Reset selection when leaving active view, switching size, or filters
  useEffect(() => { setSelectedIds(new Set()); }, [showArchive, staffSearch, pageSize, page]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkOtPolicy = async () => {
    if (selectedIds.size === 0) return;
    setBulkOtSaving(true);
    try {
      const res = await axios.post(
        `${API}/users/bulk-overtime-policy`,
        {
          user_ids: Array.from(selectedIds),
          overtime_policy: bulkOt.overtime_policy,
          overtime_weekly_threshold: clampOtThreshold(bulkOt.overtime_weekly_threshold),
          averaging_agreement_date: bulkOt.averaging_agreement_date || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: `Overtime policy updated for ${res.data?.updated_count ?? 0} staff`,
        description: bulkOt.overtime_policy === 'averaging'
          ? `Averaging agreement (${bulkOt.overtime_weekly_threshold}h/wk avg) applied.`
          : `Standard policy (${bulkOt.overtime_weekly_threshold}h/wk) applied.`,
      });
      setSelectedIds(new Set());
      setShowBulkOtDialog(false);
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Bulk overtime update failed',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setBulkOtSaving(false);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedIds.size === 0) return;
    setBulkArchiving(true);
    try {
      const res = await axios.post(
        `${API}/users/bulk-archive`,
        { user_ids: Array.from(selectedIds), archive_reason: bulkArchiveReason.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const archivedCount = res.data?.archived_count || 0;
      const skippedCount = (res.data?.skipped || []).length;
      toast({
        title: archivedCount === 0 ? 'No users archived' : `Archived ${archivedCount} user${archivedCount !== 1 ? 's' : ''}`,
        description: skippedCount
          ? `${skippedCount} user${skippedCount !== 1 ? 's were' : ' was'} skipped (already archived, super-user, or not found).`
          : 'All selected users moved to the archive.',
      });
      setSelectedIds(new Set());
      setBulkArchiveReason('');
      setShowBulkArchiveDialog(false);
      fetchUsers();
      if (showArchive) fetchArchivedUsers();
    } catch (error) {
      toast({
        title: 'Bulk archive failed',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setBulkArchiving(false);
    }
  };

  const handleResendInvitation = async (userId, userEmail) => {
    if (!window.confirm(`Resend invitation to ${userEmail}? This will generate a new temporary password.`)) return;
    
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/users/${userId}/resend-invitation`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'Invitation Sent',
        description: response.data.message || 'New invitation sent successfully with temporary password',
        duration: 6000
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to resend invitation',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendPasswordReset = async (userId, userEmail) => {
    if (!window.confirm(`Send a password reset link to ${userEmail}?\n\nThe user's current password stays valid until they click the link and set a new one. Link expires in 1 hour.`)) return;

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/users/${userId}/send-password-reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: 'Reset Link Sent',
        description: response.data.message || `Password reset link emailed to ${userEmail}`,
        duration: 6000,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send reset link',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openEditDialog = (userToEdit) => {
    setSelectedUser(userToEdit);
    setEditCountry(userToEdit.country || 'US');
    setEditData({
      name: userToEdit.name || '',
      first_name: userToEdit.first_name || (userToEdit.name || '').trim().split(/\s+/)[0] || '',
      middle_name: userToEdit.middle_name || '',
      last_name: userToEdit.last_name || (userToEdit.name || '').trim().split(/\s+/).slice(1).join(' ') || '',
      email: userToEdit.email || '',
      phone: userToEdit.phone || '',
      street_address: userToEdit.street_address || '',
      city: userToEdit.city || '',
      state: userToEdit.state || '',
      postal_code: userToEdit.postal_code || '',
      country: userToEdit.country || 'US',
      role: userToEdit.role || 'user',
      user_type: userToEdit.user_type || 'employee',
      shift_duration: userToEdit.shift_duration || 8.0,
      overtime_policy: userToEdit.overtime_policy || 'standard',
      overtime_weekly_threshold: userToEdit.overtime_weekly_threshold ?? 44,
      averaging_agreement_date: userToEdit.averaging_agreement_date || '',
      timezone: tzRegion(userToEdit.timezone_region || userToEdit.timezone || 'America/Toronto'),
      dst_mode: userToEdit.dst_mode || 'inherit',
      dst_from: userToEdit.dst_from || '',
      team_id: userToEdit.team_id || '',
      break_policy: userToEdit.break_policy || 'company_default',
      qb_payroll_exempt: !!userToEdit.qb_payroll_exempt,
      vacation_pay_mode: userToEdit.vacation_pay_mode || '',
      payroll_employee_id: userToEdit.payroll_employee_id || '',
      default_project_id: userToEdit.default_project_id || null,
      pay_basis: userToEdit.pay_basis || 'hourly',
      work_region: userToEdit.work_region || '',
      reimbursements_enabled: userToEdit.reimbursements_enabled !== false,
      employee_number: userToEdit.employee_number || '',
      bill_class: userToEdit.bill_class || '',
      role_change_reason: ''
    });
    // Lazy-fetch role history for the side panel inside the dialog.
    setShowRoleHistory(false);
    setRoleHistory([]);
    fetchRoleHistory(userToEdit.id);
    setShowEditDialog(true);
  };

  const fetchRoleHistory = async (userId) => {
    setRoleHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/users/${userId}/role-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoleHistory(r.data?.history || []);
    } catch (e) {
      // Quiet fail — history is informational; the edit still works.
      setRoleHistory([]);
    } finally {
      setRoleHistoryLoading(false);
    }
  };
  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    setIsLoading(true);
    try {
      const res = await axios.put(
        `${API}/users/${selectedUser.id}/edit`,
        { ...editData, dst_from: (editData.dst_from || '').trim() || null, work_region: (editData.work_region || '').trim().toUpperCase() || null, vacation_pay_mode: editData.vacation_pay_mode || null, payroll_employee_id: (editData.payroll_employee_id || '').trim() || null, name: [editData.first_name, editData.middle_name, editData.last_name].map((x) => (x || '').trim()).filter(Boolean).join(' ') || editData.name, overtime_weekly_threshold: clampOtThreshold(editData.overtime_weekly_threshold) },
        {
          headers: { Authorization: `Bearer ${token}` },
          // Accept 202 (approval queued) as a non-error response.
          validateStatus: (s) => s >= 200 && s < 300,
        }
      );

      if (res.status === 202 && res.data?.requires_approval) {
        toast({
          title: 'Role change pending approval',
          description: 'Other profile fields were saved. Another admin must approve the new role before it takes effect.',
        });
      } else {
        toast({
          title: 'Success',
          description: 'User information updated successfully',
        });
      }

      setShowEditDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update user',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCountryChange = (value) => {
    setEditCountry(value);
    setEditData({
      ...editData,
      country: value,
      state: ''
    });
  };

  const getRoleBadge = (role) => {
    const config = {
      admin: { label: 'Administrator', className: 'bg-[#1A2433] text-red-800 border-red-300' },
      supervisor: { label: 'Supervisor', className: 'bg-[#1A2433] text-blue-800 border-[#2D3748]' },
      user: { label: 'User', className: 'bg-[#1A2433] text-green-800 border-[#2D3748]' }
    };
    
    const badge = config[role] || config.user;
    return (
      <Badge className={`${badge.className} border font-semibold px-3 py-1`}>
        {badge.label}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const config = {
      employee: { label: 'Employee', className: 'bg-[#0F1923] text-white border-[#2D3748]' },
      contractor: { label: 'Contractor', className: 'bg-[#1A2433] text-purple-800 border-purple-300' }
    };
    
    const badge = config[type] || config.employee;
    return (
      <Badge className={`${badge.className} border px-2 py-0.5 text-xs`}>
        {badge.label}
      </Badge>
    );
  };

  const getTeamBadge = (teamId) => {
    if (!teamId) {
      return (
        <span className="inline-flex items-center space-x-1 px-3 py-1 bg-[#0F1923] text-slate-400 border border-[#2D3748] text-xs font-medium rounded-full">
          <User className="w-3 h-3" />
          <span>No Team</span>
        </span>
      );
    }
    
    const team = teams.find(t => t.id === teamId);
    if (!team) {
      return (
        <span className="inline-flex items-center space-x-1 px-3 py-1 bg-[#0F1923] text-slate-400 border border-[#2D3748] text-xs font-medium rounded-full">
          <User className="w-3 h-3" />
          <span>Unknown Team</span>
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center space-x-1 px-3 py-1 bg-[#1A2433] text-purple-700 border border-purple-300 text-xs font-semibold rounded-full">
        <Users className="w-3 h-3" />
        <span>{team.name}</span>
      </span>
    );
  };

  // Show loading spinner while authentication is being verified
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A2433] via-[#0F1923] to-[#1A2433] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-400 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A2433] via-[#0F1923] to-[#1A2433] pb-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E88E5] to-[#00C896] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/settings')}
                variant="ghost"
                className="text-white hover:bg-[#1A2433]/20 p-2"
              >
                <ArrowLeft className="w-6 h-6" />
              </Button>
              <div>
                <div className="flex items-center space-x-3">
                  <Users className="w-8 h-8" />
                  <h1 className="text-2xl font-bold">Manage Users</h1>
                </div>
                <p className="text-white/95 font-medium mt-1">Add and manage users in your organization</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
            {isAdmin && (
              <Button onClick={() => setShowTzAudit(true)} variant="outline"
                className="bg-transparent border-white/60 text-white hover:bg-white/10 font-semibold"
                data-testid="timezone-audit-btn">
                <MapPin className="w-5 h-5 mr-2" />
                Timezone Audit
              </Button>
            )}
            {isAdmin && (
              <Button onClick={openRatesDialog} variant="outline"
                className="bg-transparent border-white/60 text-white hover:bg-white/10 font-semibold"
                data-testid="set-hourly-rates-btn">
                <Clock className="w-5 h-5 mr-2" />
                Set Hourly Rates
              </Button>
            )}
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#1A2433] text-[#1E88E5] hover:bg-[#0F1923] font-semibold">
                  <Plus className="w-5 h-5 mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl">Add New User</DialogTitle>
                  <DialogDescription>
                    Add a new user to your organization. They will receive an email and SMS invitation.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddUser} className="space-y-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-slate-300">Personal Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="first_name">First Name *</Label>
                        <Input
                          id="first_name"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          required
                          className="h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="middle_name">Middle Name <span className="text-slate-500 font-normal">(optional)</span></Label>
                        <Input
                          id="middle_name"
                          name="middle_name"
                          value={formData.middle_name}
                          onChange={handleChange}
                          className="h-11"
                          data-testid="add-user-middle-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="last_name">Last Name *</Label>
                        <Input
                          id="last_name"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          required
                          className="h-11"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="+1234567890"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="h-11"
                        />
                        <p className="text-xs text-gray-500">Format: +1234567890</p>
                      </div>
                    </div>
                  </div>

                  {/* Address Information */}
                  <div className="space-y-4 border-t pt-4">
                    <h3 className="text-lg font-semibold text-slate-300">Address</h3>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country *</Label>
                      <Select value={selectedCountry} onValueChange={handleCountryChange}>
                        <SelectTrigger className="h-11" data-testid="add-user-country-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map((country) => (
                            <SelectItem key={country.value} value={country.value}>
                              {country.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="street_address">Street Address *</Label>
                      <Input
                        id="street_address"
                        name="street_address"
                        value={formData.street_address}
                        onChange={handleChange}
                        required
                        className="h-11"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          required
                          className="h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">{stateLabel} *</Label>
                        {currentCountryData.states.length > 0 ? (
                          <Select value={formData.state} onValueChange={handleStateChange}>
                            <SelectTrigger className="h-11" data-testid="add-user-state-select">
                              <SelectValue placeholder={`Select ${stateLabel.toLowerCase()}`} />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              {currentCountryData.states.map((state) => (
                                <SelectItem key={state} value={state}>
                                  {state}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            id="state"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            required
                            className="h-11"
                          />
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="postal_code">{postalCodeLabel} *</Label>
                      <Input
                        id="postal_code"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleChange}
                        required
                        className="h-11"
                      />
                    </div>
                  </div>

                  {/* Role & Type */}
                  <div className="space-y-4 border-t pt-4">
                    <h3 className="text-lg font-semibold text-slate-300">Role & Type</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="role">Role *</Label>
                        <Select
                          value={formData.role}
                          onValueChange={(value) => setFormData({ ...formData, role: value })}
                        >
                          <SelectTrigger className="h-11">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Administrator</SelectItem>
                            <SelectItem value="supervisor">Supervisor</SelectItem>
                            <SelectItem value="user">User</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="user_type">User Type *</Label>
                        <Select
                          value={formData.user_type}
                          onValueChange={(value) => setFormData({ ...formData, user_type: value, qb_payroll_exempt: value === 'contractor' })}
                        >
                          <SelectTrigger className="h-11" data-testid="add-user-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="employee">Employee</SelectItem>
                            <SelectItem value="contractor">Contractor</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2" data-testid="add-user-payroll-status-block">
                        <Label>Payroll status *</Label>
                        <Select
                          value={formData.qb_payroll_exempt ? 'off' : 'on'}
                          onValueChange={(value) => setFormData({ ...formData, qb_payroll_exempt: value === 'off' })}
                        >
                          <SelectTrigger className="h-11" data-testid="add-user-payroll-status">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="on" data-testid="add-user-payroll-on">On company payroll — pushed to QuickBooks</SelectItem>
                            <SelectItem value="off" data-testid="add-user-payroll-off">Not on payroll — tracked for work only</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">
                          {formData.qb_payroll_exempt
                            ? 'Skipped by every QuickBooks payroll push and readiness check (contractors, test accounts, staff paid elsewhere). Hours still appear in reports, approvals and CSV exports.'
                            : 'Hours are pushed to QuickBooks payroll. Choosing Contractor switches this to "Not on payroll" — change it back if you pay contractors through QuickBooks.'}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="shift_duration">Work Day Hours (Auto Clock Out) *</Label>
                        <div className="flex gap-2">
                          <Select
                            value={['2','4','6','8','10','12'].includes(String(formData.shift_duration ?? 8))
                              ? String(formData.shift_duration ?? 8)
                              : 'custom'}
                            onValueChange={(value) => {
                              if (value === 'custom') return;
                              setFormData({ ...formData, shift_duration: parseFloat(value) });
                            }}
                          >
                            <SelectTrigger className="h-11 w-36" data-testid="add-user-shift-preset">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="2">2 Hours (½ day)</SelectItem>
                              <SelectItem value="4">4 Hours (½ shift)</SelectItem>
                              <SelectItem value="6">6 Hours</SelectItem>
                              <SelectItem value="8">8 Hours (full)</SelectItem>
                              <SelectItem value="10">10 Hours</SelectItem>
                              <SelectItem value="12">12 Hours</SelectItem>
                              <SelectItem value="custom">Custom…</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            min="0.25"
                            max="24"
                            step="0.25"
                            value={formData.shift_duration ?? 8}
                            onChange={(e) => setFormData({ ...formData, shift_duration: Math.max(0.25, Math.min(24, parseFloat(e.target.value) || 0)) })}
                            className="h-11 w-24"
                            data-testid="add-user-shift-custom"
                          />
                        </div>
                        <p className="text-xs text-gray-500">
                          Auto clock-out after this many hours. Supports part-time (2 h) through long shifts (24 h). A worker&apos;s team can override this with its own default shift.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Meal breaks</Label>
                        <Select
                          value={formData.break_policy || 'company_default'}
                          onValueChange={(value) => setFormData({ ...formData, break_policy: value === 'company_default' ? '' : value })}
                        >
                          <SelectTrigger className="h-11" data-testid="add-user-break-policy">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="company_default">Company default</SelectItem>
                            <SelectItem value="unpaid">Unpaid — 30 min deducted at clock-out</SelectItem>
                            <SelectItem value="paid">Paid — breaks stay on the clock</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">
                          Company default and the break threshold live in Settings → Meal Breaks.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Overtime Policy (Payroll)</Label>
                        <Select
                          value={formData.overtime_policy || 'standard'}
                          onValueChange={(value) => setFormData({ ...formData, overtime_policy: value })}
                        >
                          <SelectTrigger className="h-11" data-testid="add-user-ot-policy">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="standard">Standard — overtime past weekly threshold</SelectItem>
                            <SelectItem value="averaging">Averaging agreement — averaged over the pay period</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Label className="text-xs">Weekly OT threshold (hours)</Label>
                            <Input
                              type="number"
                              min="1"
                              max="120"
                              step="0.5"
                              className="h-11"
                              value={formData.overtime_weekly_threshold ?? 44}
                              onChange={(e) => setFormData({ ...formData, overtime_weekly_threshold: e.target.value })}
                              onBlur={(e) => setFormData({ ...formData, overtime_weekly_threshold: clampOtThreshold(e.target.value) })}
                              data-testid="add-user-ot-threshold"
                            />
                          </div>
                          {formData.overtime_policy === 'averaging' && (
                            <div className="flex-1">
                              <Label className="text-xs">Agreement signed date</Label>
                              <Input
                                type="date"
                                className="h-11"
                                value={formData.averaging_agreement_date || ''}
                                onChange={(e) => setFormData({ ...formData, averaging_agreement_date: e.target.value })}
                                data-testid="add-user-ot-agreement-date"
                              />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          Type any weekly threshold your jurisdiction requires (e.g. 40 US FLSA, 44 Ontario ESA). 1–120 hours; half-hours allowed. Company default: {otDefaults.overtime_weekly_threshold_default}h ({otDefaults.overtime_policy_default}) — change it in Settings → Overtime &amp; Payroll.
                        </p>
                        <OtThresholdHint
                          country={formData.country}
                          state={formData.state}
                          current={formData.overtime_weekly_threshold}
                          onUse={(h) => setFormData({ ...formData, overtime_weekly_threshold: h })}
                          testId="add-user-ot-hint"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="timezone">Timezone *</Label>
                        <Select
                          value={formData.timezone}
                          onValueChange={(value) => setFormData({ ...formData, timezone: value })}
                        >
                          <SelectTrigger className="h-11">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TIMEZONE_OPTION_GROUPS.map((group) => (
                              <React.Fragment key={group.label}>
                                <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                  {group.label}
                                </div>
                                {group.options.map((opt) => (
                                  <SelectItem
                                    key={opt.value}
                                    value={opt.value}
                                    data-testid={`add-user-timezone-option-${opt.value}`}
                                  >
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </React.Fragment>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">
                          User's local timezone for time display
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="flex-1 bg-[#1E88E5] hover:bg-blue-700 h-11"
                    >
                      {isLoading ? 'Adding User...' : 'Add User & Send Invitation'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setShowAddDialog(false)}
                      variant="outline"
                      className="flex-1 h-11"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk hourly-rate editor (Jun 2026 — internal labor cost for Job Margins) */}
      <Dialog open={showRatesDialog} onOpenChange={setShowRatesDialog}>
        <DialogContent className="max-w-lg" data-testid="hourly-rates-dialog">
          <DialogHeader>
            <DialogTitle>Set Hourly Rates</DialogTitle>
            <DialogDescription>
              Internal labor cost per worker ($/hour) — used by the Job Margins report. Never shown to workers. Leave blank to clear.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto space-y-1 pr-1">
            {rateRows.map((r, i) => (
              <div key={r.id} className="flex items-center justify-between gap-3 py-1.5 border-b border-[#2D3748]/50">
                <span className="text-sm text-gray-100 truncate">
                  {r.name} <span className="text-xs text-slate-500">({r.role})</span>
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-slate-500 text-sm">$</span>
                  <Input
                    type="number" min="0" max="1000" step="0.25" placeholder="—"
                    className="w-24 h-8"
                    value={r.hourly_rate}
                    onChange={(e) => setRateRows((rows) => rows.map((x, j) => (j === i ? { ...x, hourly_rate: e.target.value } : x)))}
                    data-testid={`rate-input-${r.id}`}
                  />
                  <span className="text-slate-500 text-xs">/h</span>
                </div>
              </div>
            ))}
            {rateRows.length === 0 && <p className="text-sm text-slate-500 py-4 text-center">No active workers.</p>}
          </div>
          <div className="flex gap-3 pt-2">
            <Button onClick={saveRates} disabled={ratesSaving} className="flex-1 bg-[#1E88E5] hover:bg-blue-700 text-white" data-testid="save-rates-btn">
              {ratesSaving ? 'Saving…' : 'Save All Rates'}
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => setShowRatesDialog(false)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* User Stats */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-6">
          <Card className="border-2 border-[#2D3748] bg-[#0F1923]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">{users.length}</p>
              <p className="text-sm text-[#1E88E5] font-medium">Total Users</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-[#2D3748] bg-[#0F1923] cursor-pointer" onClick={() => setPayrollFilter(payrollFilter === 'on' ? 'all' : 'on')} data-testid="staff-tile-on-payroll" title="Click to filter">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-500">{users.filter((u) => !u.qb_payroll_exempt).length}</p>
              <p className="text-sm text-[#00C896] font-medium">On payroll</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-[#2D3748] bg-[#0F1923] cursor-pointer" onClick={() => setPayrollFilter(payrollFilter === 'off' ? 'all' : 'off')} data-testid="staff-tile-not-on-payroll" title="Click to filter">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-sky-400">{users.filter((u) => u.qb_payroll_exempt).length}</p>
              <p className="text-sm text-[#7DD3FC] font-medium">Not on payroll</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-[#2D3748] bg-[#0F1923]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-700">
                {users.filter(u => u.role === 'admin').length}
              </p>
              <p className="text-sm text-[#EF4444] font-medium">Administrators</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-[#2D3748] bg-[#0F1923]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-700">
                {users.filter(u => u.role === 'supervisor').length}
              </p>
              <p className="text-sm text-[#A78BFA] font-medium">Supervisors</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-[#2D3748] bg-[#0F1923]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-700">
                {users.filter(u => u.role === 'user').length}
              </p>
              <p className="text-sm text-[#00C896] font-medium">Users</p>
            </CardContent>
          </Card>
        </div>

        <NameSweepBanner token={token} onFixed={fetchUsers} />

        {/* Users List */}
        <Card className="shadow-lg">
          <CardHeader className="bg-[#1A2433] border-b">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="text-xl font-bold">
                {showArchive ? `Archived Users (${archivedUsers.length})` : `Active Staff (${users.length})`}
              </CardTitle>
              <div className="flex items-center gap-2">
                {!showArchive && (
                  <select value={payrollFilter} onChange={(e) => setPayrollFilter(e.target.value)} className="h-9 rounded-md px-2 text-xs"
                    style={{ background: '#0F1923', color: '#E2E8F0', border: '1px solid #2D3748' }} title="Filter by payroll status" data-testid="staff-payroll-filter">
                    <option value="all">All staff</option>
                    <option value="on">On payroll</option>
                    <option value="off">Not on payroll</option>
                  </select>
                )}
                {!showArchive && (
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search staff..."
                      value={staffSearch}
                      onChange={(e) => setStaffSearch(e.target.value)}
                      className="pl-8 h-9 w-44 sm:w-56"
                      data-testid="staff-search-input"
                    />
                  </div>
                )}
                <Button
                  onClick={() => {
                    const next = !showArchive;
                    setShowArchive(next);
                    if (next) fetchArchivedUsers();
                  }}
                  variant={showArchive ? 'default' : 'outline'}
                  size="sm"
                  className={showArchive ? 'bg-[#1E88E5] hover:bg-blue-700' : ''}
                  data-testid="users-archive-toggle"
                >
                  <Archive className="w-4 h-4 mr-1.5" />
                  {showArchive ? 'View Active' : 'View Archive'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {showArchive ? (
              loadingArchive ? (
                <div className="p-12 text-center text-slate-400">Loading archived users…</div>
              ) : archivedUsers.length === 0 ? (
                <div className="p-12 text-center" data-testid="archive-empty">
                  <Archive className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500 text-lg">No archived users</p>
                  <p className="text-sm text-slate-500 mt-2">Users you archive will appear here. Records are retained for 7 years.</p>
                </div>
              ) : (
                <>
                  <div className="divide-y" data-testid="archived-users-list">
                    {archivedUsers
                      .slice((archivePage - 1) * pageSize, archivePage * pageSize)
                      .map((u) => (
                        <div key={u.id} className="p-4 sm:p-5 hover:bg-[#1A2433] transition-colors" data-testid={`archived-user-${u.id}`}>
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-3 mb-2 flex-wrap">
                                <h3 className="text-lg font-bold text-white">{u.name}</h3>
                                <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-amber-500/15 text-amber-400">Archived</span>
                                {getRoleBadge(u.role)}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-400">
                                <div className="flex items-center space-x-2"><Mail className="w-4 h-4 flex-shrink-0" /><span className="truncate">{u.email}</span></div>
                                {u.deleted_at && (
                                  <div className="text-xs">Archived {new Date(u.deleted_at).toLocaleDateString()} by {u.deleted_by_name || 'admin'}</div>
                                )}
                                {u.archive_reason && (
                                  <div className="text-xs col-span-full italic" style={{ color: '#A78BFA' }}>Reason: {u.archive_reason}</div>
                                )}
                              </div>
                            </div>
                            <Button
                              onClick={() => handleRestoreUser(u.id, u.name)}
                              variant="outline"
                              size="sm"
                              className="flex items-center justify-center gap-1.5 h-9 px-3 text-[#00C896] hover:bg-[#0F1923] border-[#2D3748] hover:border-green-400"
                              data-testid={`restore-user-${u.id}`}
                            >
                              <RotateCcw className="w-4 h-4" />
                              <span className="text-xs sm:text-sm font-medium">Restore</span>
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                  <Pagination
                    total={archivedUsers.length}
                    page={archivePage}
                    pageSize={pageSize}
                    onPageChange={setArchivePage}
                    onPageSizeChange={setPageSize}
                    testId="archived-users-pagination"
                    itemLabel="archived user"
                  />
                </>
              )
            ) : users.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 text-lg">No users yet</p>
                <Button
                  onClick={() => setShowAddDialog(true)}
                  className="mt-4 bg-[#1E88E5] hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add First User
                </Button>
              </div>
            ) : (
              <>
                {/* Bulk action bar (visible when at least one row is selected) */}
                {selectedIds.size > 0 && (
                  <div
                    className="flex items-center justify-between gap-3 px-4 py-3 border-b"
                    style={{ background: 'rgba(30,136,229,0.10)', borderColor: '#2D3748' }}
                    data-testid="bulk-action-bar"
                  >
                    <p className="text-sm text-white font-medium">
                      <span data-testid="bulk-selected-count">{selectedIds.size}</span> selected
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedIds(new Set())}
                        data-testid="bulk-clear-btn"
                      >
                        Clear
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setShowBulkOtDialog(true)}
                        className="bg-teal-600 hover:bg-teal-700 text-white"
                        data-testid="bulk-ot-policy-btn"
                      >
                        <Clock className="w-4 h-4 mr-1.5" />
                        Set OT policy
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setShowBulkArchiveDialog(true)}
                        className="bg-[#EF4444] hover:bg-red-700 text-white"
                        data-testid="bulk-archive-btn"
                      >
                        <Archive className="w-4 h-4 mr-1.5" />
                        Archive selected
                      </Button>
                    </div>
                  </div>
                )}
                <div className="divide-y">
                  {(() => {
                    const filtered = users.filter(staffMatches);
                    const slice = filtered.slice((page - 1) * pageSize, page * pageSize);
                    if (filtered.length === 0) {
                      return (
                        <div className="p-12 text-center text-slate-400" data-testid="staff-search-empty">
                          No staff match "{staffSearch}"
                        </div>
                      );
                    }
                    // Select-all row
                    const archivableOnPage = slice.filter((u) => u.id !== user.id && !u.is_super_user);
                    const allSelected = archivableOnPage.length > 0 && archivableOnPage.every((u) => selectedIds.has(u.id));
                    const someSelected = archivableOnPage.some((u) => selectedIds.has(u.id));
                    const selectAllRow = (
                      <div
                        key="__select_all"
                        className="px-4 py-2 flex items-center gap-3 text-xs font-semibold uppercase tracking-wider"
                        style={{ background: 'rgba(255,255,255,0.02)', color: '#A0AEC0' }}
                      >
                        <input
                          type="checkbox"
                          aria-label="Select all on this page"
                          ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                          checked={allSelected}
                          onChange={(e) => {
                            setSelectedIds((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) archivableOnPage.forEach((u) => next.add(u.id));
                              else archivableOnPage.forEach((u) => next.delete(u.id));
                              return next;
                            });
                          }}
                          className="w-4 h-4 cursor-pointer accent-blue-500"
                          data-testid="select-all-checkbox"
                        />
                        <span>Select all on this page ({archivableOnPage.length})</span>
                      </div>
                    );
                    const rows = slice.map((u) => {
                      const checkable = u.id !== user.id && !u.is_super_user;
                      return (
                  <div key={u.id} className="p-4 sm:p-5 hover:bg-[#1A2433] transition-colors flex items-start gap-3">
                    <input
                      type="checkbox"
                      aria-label={`Select ${u.name}`}
                      checked={selectedIds.has(u.id)}
                      disabled={!checkable}
                      onChange={() => checkable && toggleSelect(u.id)}
                      className="mt-2 w-4 h-4 cursor-pointer accent-blue-500 disabled:cursor-not-allowed disabled:opacity-30"
                      data-testid={`select-user-${u.id}`}
                      title={!checkable ? "You can't bulk-archive yourself or super-users" : 'Select for bulk action'}
                    />
                    <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-2 flex-wrap">
                          <h3 className="text-lg font-bold text-white">{u.name}</h3>
                          {u.is_super_user && (
                            <span className="px-3 py-1 bg-gradient-to-r from-[#A78BFA] to-pink-600 text-white text-xs font-bold rounded-full">
                              SUPER USER
                            </span>
                          )}
                          {getRoleBadge(u.role)}
                          {getTypeBadge(u.user_type)}
                          {getTeamBadge(u.team_id)}
                          {u.qb_payroll_exempt && (
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: 'rgba(56,189,248,0.12)', color: '#7DD3FC', border: '1px solid rgba(56,189,248,0.35)' }}
                              title="Not on payroll — skipped by every QuickBooks payroll push and readiness check (Edit → Payroll status)" data-testid={`user-qb-exempt-badge-${u.id}`}>
                              NOT ON PAYROLL
                            </span>
                          )}
                          {(jobStats[u.id]?.active ?? 0) > 0 && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold cursor-default"
                              style={{
                                background: 'rgba(30,136,229,0.12)',
                                color: '#7DD3FC',
                                border: '1px solid rgba(30,136,229,0.3)',
                              }}
                              title={
                                `${jobStats[u.id].active} active job${jobStats[u.id].active === 1 ? '' : 's'}` +
                                (jobStats[u.id].in_progress
                                  ? ` (${jobStats[u.id].in_progress} in progress)`
                                  : '')
                              }
                              data-testid={`user-active-jobs-${u.id}`}
                            >
                              <Briefcase className="w-3 h-3" />
                              {jobStats[u.id].active} active
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-400">
                          <div className="flex items-center space-x-2 break-all">
                            <Mail className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{u.email}</span>
                          </div>
                          {u.phone && (
                            <div className="flex items-center space-x-2">
                              <Phone className="w-4 h-4 flex-shrink-0" />
                              <span>{u.phone}</span>
                            </div>
                          )}
                          {u.address && (
                            <div className="flex items-center space-x-2 col-span-full">
                              <MapPin className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">{u.address}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {u.id !== user.id && (
                        <div className="flex flex-col sm:flex-row gap-2 mt-2 sm:mt-0 sm:ml-4">
                          <Button
                            onClick={() => openStatsModal(u)}
                            variant="outline"
                            size="sm"
                            className="flex items-center justify-center gap-1.5 h-9 px-3 text-[#A78BFA] hover:bg-[#0F1923] hover:text-purple-700 border-[#2D3748] hover:border-purple-400 min-w-[90px]"
                            title="View job stats"
                            data-testid={`stats-btn-${u.id}`}
                          >
                            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                            <span className="text-xs sm:text-sm font-medium">Stats</span>
                          </Button>
                          <Button
                            onClick={() => navigate(`/reports?report=attendance-individual&user_id=${u.id}&name=${encodeURIComponent(u.name || '')}`)}
                            variant="outline"
                            size="sm"
                            className="flex items-center justify-center gap-1.5 h-9 px-3 text-[#1E88E5] hover:bg-[#0F1923] hover:text-blue-400 border-[#2D3748] hover:border-blue-400 min-w-[90px]"
                            title={`Open ${u.name}'s individual attendance report`}
                            data-testid={`attendance-btn-${u.id}`}
                          >
                            <CalendarCheck className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                            <span className="text-xs sm:text-sm font-medium">Attendance</span>
                          </Button>
                          <Button
                            onClick={() => openEditDialog(u)}
                            variant="outline"
                            size="sm"
                            className="flex items-center justify-center gap-1.5 h-9 px-3 text-[#00C896] hover:bg-[#0F1923] hover:text-green-700 border-[#2D3748] hover:border-green-400 min-w-[90px]"
                            title="Edit user information"
                            disabled={u.is_super_user && !user.is_super_user}
                            data-testid={`edit-user-btn-${u.id}`}
                          >
                            <Edit className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                            <span className="text-xs sm:text-sm font-medium">Edit</span>
                          </Button>
                          <Button
                            onClick={() => handleResendInvitation(u.id, u.email)}
                            variant="outline"
                            size="sm"
                            className="flex items-center justify-center gap-1.5 h-9 px-3 text-[#1E88E5] hover:bg-[#0F1923] hover:text-blue-700 border-[#2D3748] hover:border-[#2D3748] min-w-[90px]"
                            title="Resend invitation with new temporary password (overwrites current password)"
                          >
                            <Send className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                            <span className="text-xs sm:text-sm font-medium">Resend</span>
                          </Button>
                          <Button
                            onClick={() => handleSendPasswordReset(u.id, u.email)}
                            variant="outline"
                            size="sm"
                            className="flex items-center justify-center gap-1.5 h-9 px-3 text-[#A78BFA] hover:bg-[#0F1923] hover:text-purple-700 border-[#2D3748] hover:border-purple-400 min-w-[110px]"
                            title="Send a password-reset link (does NOT overwrite current password)"
                            data-testid={`send-reset-link-${u.id}`}
                          >
                            <KeyRound className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                            <span className="text-xs sm:text-sm font-medium">Reset Link</span>
                          </Button>
                          <Button
                            onClick={() => handleDeleteUser(u.id, u.name)}
                            variant="outline"
                            size="sm"
                            className="flex items-center justify-center gap-1.5 h-9 px-3 text-[#EF4444] hover:bg-[#0F1923] hover:text-red-700 border-red-300 hover:border-red-400 min-w-[90px]"
                            disabled={u.is_super_user}
                            title={u.is_super_user ? "Super User account cannot be archived" : "Archive user (kept for 7 years, can be restored)"}
                          >
                            <Archive className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                            <span className="text-xs sm:text-sm font-medium">Archive</span>
                          </Button>
                        </div>
                      )}
                    </div>
                    </div>
                  </div>
                      );
                    });
                    return [selectAllRow, ...rows];
                  })()}
                </div>
                <Pagination
                  total={users.filter(staffMatches).length}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                  testId="users-pagination"
                  itemLabel="staff member"
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Stats Modal */}
        <Dialog
          open={!!statsUser}
          onOpenChange={(o) => { if (!o) { setStatsUser(null); setStatsData(null); } }}
        >
          <DialogContent
            className="max-w-xl"
            data-testid="user-stats-modal"
            style={{ background: '#0F1923', border: '1px solid #2D3748', color: '#F8FAFC' }}
          >
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5" style={{ color: '#A78BFA' }} />
                {statsUser?.name || 'Job stats'}
              </DialogTitle>
              <DialogDescription style={{ color: '#94A3B8' }}>
                Job activity for this team member · pulled live
              </DialogDescription>
            </DialogHeader>

            {statsLoading || !statsData ? (
              <div className="py-10 text-center text-sm" style={{ color: '#94A3B8' }}>
                Loading…
              </div>
            ) : (
              <div className="space-y-4">
                {/* Top stats grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { label: 'Active',         value: statsData.active,             color: '#7DD3FC', bg: 'rgba(30,136,229,0.12)' },
                    { label: 'In Progress',    value: statsData.in_progress,        color: '#FCD34D', bg: 'rgba(245,158,11,0.12)' },
                    { label: 'Awaiting Accept',value: statsData.pending_acceptance, color: '#A78BFA', bg: 'rgba(167,139,250,0.12)' },
                    { label: 'Completed',      value: statsData.completed,          color: '#86EFAC', bg: 'rgba(16,185,129,0.12)' },
                  ].map((s) => (
                    <div
                      key={s.label}
                      className="p-3 rounded-lg"
                      style={{ background: s.bg, border: `1px solid ${s.color}33` }}
                      data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: s.color }}>
                        {s.label}
                      </div>
                      <div className="text-2xl font-bold mt-1 text-white">{s.value}</div>
                    </div>
                  ))}
                </div>

                {/* Completed hours */}
                {statsData.total_completed_hours > 0 && (
                  <div
                    className="p-3 rounded-lg flex items-center gap-2"
                    style={{ background: '#1A2433', border: '1px solid #2D3748' }}
                  >
                    <Clock className="w-4 h-4" style={{ color: '#86EFAC' }} />
                    <span className="text-sm text-white">
                      <strong>{statsData.total_completed_hours}h</strong>
                      <span style={{ color: '#94A3B8' }}> logged across completed jobs</span>
                    </span>
                  </div>
                )}

                {/* Recent jobs list */}
                {statsData.recent_jobs?.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: '#64748B' }}>
                      Recent activity
                    </div>
                    <ul className="space-y-1.5">
                      {statsData.recent_jobs.map((j) => (
                        <li
                          key={j.id}
                          className="flex items-center justify-between p-2 rounded"
                          style={{ background: '#1A2433', border: '1px solid #2D3748' }}
                          data-testid={`recent-job-${j.id}`}
                        >
                          <div className="min-w-0 flex-1 mr-2">
                            <div className="text-sm text-white truncate">{j.title || 'Untitled'}</div>
                            <div className="text-[10px]" style={{ color: '#64748B' }}>
                              {j.completed_at ? new Date(j.completed_at).toLocaleDateString() : (j.updated_at ? new Date(j.updated_at).toLocaleDateString() : '')}
                            </div>
                          </div>
                          <span
                            className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                            style={{
                              color: j.status === 'completed' ? '#86EFAC'
                                   : j.status === 'in_progress' ? '#FCD34D'
                                   : j.status === 'rejected' || j.status === 'cancelled' ? '#FCA5A5'
                                   : '#7DD3FC',
                              background: j.status === 'completed' ? 'rgba(16,185,129,0.12)'
                                       : j.status === 'in_progress' ? 'rgba(245,158,11,0.12)'
                                       : j.status === 'rejected' || j.status === 'cancelled' ? 'rgba(239,68,68,0.12)'
                                       : 'rgba(30,136,229,0.12)',
                            }}
                          >
                            {j.status.replace('_', ' ')}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-wrap justify-between items-center gap-2 pt-2">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      onClick={() => { navigate(`/jobs?assigned_to=${statsUser.id}`); setStatsUser(null); }}
                      className="text-[#7DD3FC] border-[#2D3748] hover:bg-[#0F1923]"
                      data-testid="stats-view-jobs"
                    >
                      View {statsUser?.name?.split(' ')[0]}'s jobs →
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { navigate(`/reports?report=attendance-individual&user_id=${statsUser.id}&name=${encodeURIComponent(statsUser.name || '')}`); setStatsUser(null); }}
                      className="text-[#1E88E5] border-[#2D3748] hover:bg-[#0F1923]"
                      data-testid="stats-view-attendance"
                    >
                      <CalendarCheck className="w-4 h-4 mr-1.5" /> Attendance report →
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => { setStatsUser(null); setStatsData(null); }}
                    className="text-slate-400"
                    data-testid="stats-close"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit User Information</DialogTitle>
              <DialogDescription>
                Update user details for {selectedUser?.name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={editData.first_name}
                    onChange={(e) => setEditData({...editData, first_name: e.target.value})}
                    required
                    data-testid="edit-user-first-name"
                  />
                </div>
                <div>
                  <Label>Middle Name <span className="text-slate-500 font-normal">(optional)</span></Label>
                  <Input
                    value={editData.middle_name}
                    onChange={(e) => setEditData({...editData, middle_name: e.target.value})}
                    data-testid="edit-user-middle-name"
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input
                    value={editData.last_name}
                    onChange={(e) => setEditData({...editData, last_name: e.target.value})}
                    required
                    data-testid="edit-user-last-name"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-500 -mt-2" data-testid="edit-user-name-preview">
                Display name: <span className="text-slate-200 font-medium">{[editData.first_name, editData.middle_name, editData.last_name].map((x) => (x || '').trim()).filter(Boolean).join(' ') || '—'}</span> — this is what Staff, payroll and QuickBooks matching use, so spell it exactly as the employee appears in QuickBooks.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={editData.email}
                    onChange={(e) => setEditData({...editData, email: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={editData.phone}
                    onChange={(e) => setEditData({...editData, phone: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Street Address</Label>
                <Input
                  value={editData.street_address}
                  onChange={(e) => setEditData({...editData, street_address: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>City</Label>
                  <Input
                    value={editData.city}
                    onChange={(e) => setEditData({...editData, city: e.target.value})}
                  />
                </div>
                <div>
                  <Label>State/Province</Label>
                  <Input
                    value={editData.state}
                    onChange={(e) => setEditData({...editData, state: e.target.value})}
                    data-testid="edit-user-state-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Postal Code</Label>
                  <Input
                    value={editData.postal_code}
                    onChange={(e) => setEditData({...editData, postal_code: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Country</Label>
                  <Select value={editCountry} onValueChange={handleEditCountryChange}>
                    <SelectTrigger data-testid="edit-user-country-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Role</Label>
                    <button
                      type="button"
                      className="text-xs text-blue-500 hover:text-blue-400 underline"
                      onClick={() => setShowRoleHistory((v) => !v)}
                      data-testid="toggle-role-history-btn"
                    >
                      {showRoleHistory ? 'Hide history' : `History (${roleHistory.length})`}
                    </button>
                  </div>
                  <Select value={editData.role} onValueChange={(value) => setEditData({...editData, role: value})}>
                    <SelectTrigger data-testid="edit-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                      <SelectItem value="admin">Administrator</SelectItem>
                    </SelectContent>
                  </Select>
                  {editData.role !== (selectedUser?.role || 'user') && (
                    <Input
                      placeholder="Reason for role change (optional)"
                      value={editData.role_change_reason}
                      onChange={(e) => setEditData({ ...editData, role_change_reason: e.target.value })}
                      className="mt-2 text-xs"
                      data-testid="role-change-reason-input"
                    />
                  )}
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={editData.user_type} onValueChange={(value) => setEditData({...editData, user_type: value, qb_payroll_exempt: value === 'contractor' ? true : (editData.user_type === 'contractor' ? false : editData.qb_payroll_exempt)})}>
                    <SelectTrigger data-testid="edit-user-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="contractor">Contractor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div data-testid="edit-user-payroll-status-block">
                <Label>Payroll status</Label>
                <Select value={editData.qb_payroll_exempt ? 'off' : 'on'} onValueChange={(value) => setEditData({ ...editData, qb_payroll_exempt: value === 'off' })}>
                  <SelectTrigger data-testid="edit-user-payroll-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on" data-testid="edit-user-payroll-on">On company payroll — pushed to QuickBooks</SelectItem>
                    <SelectItem value="off" data-testid="edit-user-payroll-off">Not on payroll — tracked for work only</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {editData.qb_payroll_exempt
                    ? 'Skipped by every QuickBooks payroll push and readiness check (contractors, test accounts, staff paid elsewhere). Hours still appear in reports, approvals and CSV exports — only the QuickBooks push changes.'
                    : 'Hours are pushed to QuickBooks payroll. Switching Type to Contractor sets this to "Not on payroll" — change it back if you pay contractors through QuickBooks.'}
                </p>
              </div>

              {showRoleHistory && (
                <div className="rounded-lg border-2 border-slate-700 bg-slate-900/50 p-3 space-y-2" data-testid="role-history-panel">
                  <div className="text-xs font-semibold text-slate-200 uppercase tracking-wide">Role &amp; overtime change history</div>
                  {roleHistoryLoading && <div className="text-xs text-slate-400">Loading…</div>}
                  {!roleHistoryLoading && roleHistory.length === 0 && (
                    <div className="text-xs text-slate-400">No role or overtime changes recorded yet.</div>
                  )}
                  {!roleHistoryLoading && roleHistory.length > 0 && (
                    <div className="space-y-1.5 max-h-44 overflow-auto">
                      {roleHistory.map((h) => (
                        <div key={h.id} className="text-xs text-slate-200 flex items-start justify-between gap-3 border-b border-slate-700/50 pb-1.5 last:border-0" data-testid={h.kind === 'overtime_policy' ? `ot-history-${h.id}` : `role-history-${h.id}`}>
                          <div className="min-w-0">
                            {h.kind === 'overtime_policy' ? (
                              <>
                                <span className="text-orange-300 font-semibold mr-1">OT</span>
                                <span className="font-mono text-slate-400">{h.old_ot?.overtime_policy || '∅'} {h.old_ot?.overtime_weekly_threshold ?? '—'}h</span>
                                <span className="mx-1 text-slate-500">→</span>
                                <span className="font-mono text-emerald-300">{h.new_ot?.overtime_policy} {h.new_ot?.overtime_weekly_threshold ?? '—'}h</span>
                                {h.new_ot?.averaging_agreement_date !== h.old_ot?.averaging_agreement_date && (
                                  <span className="text-slate-400 ml-2 italic">agreement {h.new_ot?.averaging_agreement_date || 'cleared'}</span>
                                )}
                                {h.source === 'bulk_overtime_policy' && <span className="text-slate-500 ml-2">(bulk)</span>}
                              </>
                            ) : (
                              <>
                                <span className="font-mono text-slate-400">{h.old_role || '∅'}</span>
                                <span className="mx-1 text-slate-500">→</span>
                                <span className="font-mono text-emerald-300">{h.new_role}</span>
                                {h.reason && <span className="text-slate-400 ml-2 italic">{h.reason}</span>}
                              </>
                            )}
                          </div>
                          <div className="text-right shrink-0 text-slate-500">
                            <div>{h.actor_name || h.actor_email}</div>
                            <div>{new Date(h.created_at).toLocaleString()}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label>Work Day Hours (Auto Clock Out)</Label>
                <div className="flex gap-2">
                  <Select
                    value={['2','4','6','8','10','12'].includes(String(editData.shift_duration ?? 8))
                      ? String(editData.shift_duration ?? 8)
                      : 'custom'}
                    onValueChange={(value) => {
                      if (value === 'custom') return;
                      setEditData({ ...editData, shift_duration: parseFloat(value) });
                    }}
                  >
                    <SelectTrigger className="w-36" data-testid="edit-user-shift-preset">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2">2 Hours (½ day)</SelectItem>
                      <SelectItem value="4">4 Hours (½ shift)</SelectItem>
                      <SelectItem value="6">6 Hours</SelectItem>
                      <SelectItem value="8">8 Hours (full)</SelectItem>
                      <SelectItem value="10">10 Hours</SelectItem>
                      <SelectItem value="12">12 Hours</SelectItem>
                      <SelectItem value="custom">Custom…</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="0.25"
                    max="24"
                    step="0.25"
                    value={editData.shift_duration ?? 8}
                    onChange={(e) => setEditData({ ...editData, shift_duration: Math.max(0.25, Math.min(24, parseFloat(e.target.value) || 0)) })}
                    className="w-24"
                    data-testid="edit-user-shift-custom"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Auto clock-out after this many hours. Supports part-time (2 h) through long shifts (24 h). A worker&apos;s team can override this with its own default shift.
                </p>
              </div>

              <div>
                <Label>Overtime Policy (Payroll)</Label>
                <Select
                  value={editData.overtime_policy || 'standard'}
                  onValueChange={(value) => setEditData({ ...editData, overtime_policy: value })}
                >
                  <SelectTrigger data-testid="edit-user-ot-policy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard — overtime past weekly threshold</SelectItem>
                    <SelectItem value="averaging">Averaging agreement — averaged over the pay period</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2 mt-2">
                  <div className="flex-1">
                    <Label className="text-xs">Weekly OT threshold (hours)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="120"
                      step="0.5"
                      value={editData.overtime_weekly_threshold ?? 44}
                      onChange={(e) => setEditData({ ...editData, overtime_weekly_threshold: e.target.value })}
                      onBlur={(e) => setEditData({ ...editData, overtime_weekly_threshold: clampOtThreshold(e.target.value) })}
                      data-testid="edit-user-ot-threshold"
                    />
                  </div>
                  {editData.overtime_policy === 'averaging' && (
                    <div className="flex-1">
                      <Label className="text-xs">Agreement signed date</Label>
                      <Input
                        type="date"
                        value={editData.averaging_agreement_date || ''}
                        onChange={(e) => setEditData({ ...editData, averaging_agreement_date: e.target.value })}
                        data-testid="edit-user-ot-agreement-date"
                      />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {editData.overtime_policy === 'averaging'
                    ? 'Signed averaging agreement: overtime only applies when total pay-period hours exceed threshold × weeks (e.g. 88h over a bi-weekly period at 44h/wk). Set the threshold to whatever the agreement says — up to 120h/week. QuickBooks pushes send these hours as Regular until the averaged cap is exceeded.'
                    : 'Overtime applies past this many hours in a single week. Payroll Workbench and QuickBooks pushes both follow this setting.'}
                </p>
                <OtThresholdHint
                  country={editData.country}
                  state={editData.state}
                  current={editData.overtime_weekly_threshold}
                  onUse={(h) => setEditData({ ...editData, overtime_weekly_threshold: h })}
                  testId="edit-user-ot-hint"
                />
              </div>

              <div>
                <Label>Meal breaks</Label>
                <Select
                  value={editData.break_policy || 'company_default'}
                  onValueChange={(value) => setEditData({ ...editData, break_policy: value })}
                >
                  <SelectTrigger data-testid="edit-user-break-policy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company_default">Company default</SelectItem>
                    <SelectItem value="unpaid">Unpaid — 30 min deducted at clock-out</SelectItem>
                    <SelectItem value="paid">Paid — breaks stay on the clock</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {(editData.break_policy || 'company_default') === 'paid'
                    ? 'Paid: no break prompt at clock-out and nothing is deducted — break time stays on the clock.'
                    : 'Unpaid: at clock-out, workers past the break threshold confirm their meal break and 30 minutes is deducted with an audit note. Company default & threshold live in Settings → Meal Breaks.'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3" data-testid="edit-user-payroll-extra">
                <div>
                  <Label>Vacation pay</Label>
                  <Select value={editData.vacation_pay_mode || 'company'} onValueChange={(value) => setEditData({ ...editData, vacation_pay_mode: value === 'company' ? '' : value })}>
                    <SelectTrigger data-testid="edit-user-vacation-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company">Company rule (Settings → Payroll Rules)</SelectItem>
                      <SelectItem value="accrue">Accrue balance</SelectItem>
                      <SelectItem value="payout">Pay out every period (seasonal)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payroll employee ID</Label>
                  <Input value={editData.payroll_employee_id || ''} onChange={(e) => setEditData({ ...editData, payroll_employee_id: e.target.value })} placeholder="ID in your payroll provider" data-testid="edit-user-payroll-employee-id" />
                </div>
                <div>
                  <Label>Pay basis</Label>
                  <Select value={editData.pay_basis || 'hourly'} onValueChange={(v) => setEditData({ ...editData, pay_basis: v })}>
                    <SelectTrigger data-testid="edit-user-pay-basis"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hourly">Hourly — paid by the hour on a project</SelectItem>
                      <SelectItem value="service">Service — paid by job hours under a project</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Groups this worker in the Hours Review report.</p>
                </div>
                <div>
                  <Label>Work province / state <span className="text-xs text-slate-400">(call-out minimum)</span></Label>
                  <Select value={editData.work_region || '__none__'} onValueChange={(v) => setEditData({ ...editData, work_region: v === '__none__' ? '' : v })}>
                    <SelectTrigger data-testid="edit-user-work-region"><SelectValue placeholder="Company default" /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="__none__">Company default</SelectItem>
                      {['AB', 'BC', 'SK', 'MB', 'ON', 'QC', 'NB', 'NS', 'PE', 'NL', 'YT', 'NT', 'NU', 'NY', 'CA', 'MA', 'NH', 'NJ', 'CT', 'RI', 'OR', 'WA'].map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Default project <span className="text-xs text-slate-400">(prefills admin week entry)</span></Label>
                  <Select value={editData.default_project_id || '__none__'} onValueChange={(v) => setEditData({ ...editData, default_project_id: v === '__none__' ? null : v })}>
                    <SelectTrigger data-testid="edit-user-default-project"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="__none__">None — pick per day</SelectItem>
                      {editProjects.map((p) => (
                        <SelectItem key={p.id} value={p.id} data-testid={`edit-user-default-project-${p.id}`}>
                          {p.project_number ? `${p.project_number} · ` : ''}{p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Set this when the worker is on one project every day of a pay period.</p>
                </div>
                <div className="col-span-2 flex items-center justify-between rounded-lg border border-[#1F2D3F] px-3 py-2">
                  <div>
                    <Label>Reimbursements</Label>
                    <p className="text-xs text-slate-500">Can submit receipt claims for authorized company purchases.</p>
                  </div>
                  <Switch checked={editData.reimbursements_enabled !== false} onCheckedChange={(v) => setEditData({ ...editData, reimbursements_enabled: !!v })} data-testid="edit-user-reimbursements-enabled" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3" data-testid="edit-user-billing-block">
                <div>
                  <Label>Employee number</Label>
                  <Input value={editData.employee_number || ''} onChange={(e) => setEditData({ ...editData, employee_number: e.target.value })}
                    placeholder="e.g. 90428475" data-testid="edit-user-employee-number" />
                  <p className="text-[11px] text-slate-500 mt-1">Printed as "90428475-Name" on daily site invoices.</p>
                </div>
                <div>
                  <Label>Bill class (rate-card code)</Label>
                  <Input value={editData.bill_class || ''} onChange={(e) => setEditData({ ...editData, bill_class: e.target.value })}
                    placeholder="e.g. SUPV, Cust3, TR1" data-testid="edit-user-bill-class" />
                  <p className="text-[11px] text-slate-500 mt-1">Matches a code on the customer's rate card (Customers → edit).</p>
                </div>
              </div>

              <div>
                <Label>Timezone</Label>
                <Select 
                  value={editData.timezone || 'America/Toronto'} 
                  onValueChange={(value) => setEditData({...editData, timezone: value})}
                >
                  <SelectTrigger data-testid="edit-user-timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONE_OPTION_GROUPS.map((group) => (
                      <React.Fragment key={group.label}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                          {group.label}
                        </div>
                        {group.options.map((opt) => (
                          <SelectItem
                            key={opt.value}
                            value={opt.value}
                            data-testid={`edit-user-timezone-option-${opt.value}`}
                          >
                            {opt.label}
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  User's local timezone for time display in reports
                </p>
              </div>

              <div className="rounded-lg border border-[#2D3748] p-3 space-y-2" data-testid="edit-user-dst-block">
                <Label>Daylight saving time</Label>
                <Select value={editData.dst_mode || 'inherit'} onValueChange={(value) => setEditData({ ...editData, dst_mode: value, dst_from: (value === 'inherit' || value === 'auto') ? '' : editData.dst_from })}>
                  <SelectTrigger data-testid="edit-user-dst-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit" data-testid="edit-user-dst-option-inherit">Follow company setting</SelectItem>
                    <SelectItem value="auto" data-testid="edit-user-dst-option-auto">Changes clocks (normal DST rules)</SelectItem>
                    <SelectItem value="standard" data-testid="edit-user-dst-option-standard">No DST — standard (winter) clock all year, e.g. Alberta MST</SelectItem>
                    <SelectItem value="daylight" data-testid="edit-user-dst-option-daylight">No DST — summer (daylight) clock all year, e.g. BC PDT</SelectItem>
                  </SelectContent>
                </Select>
                {(editData.dst_mode === 'standard' || editData.dst_mode === 'daylight') && (
                  <div>
                    <Label className="text-xs">Applies from (optional)</Label>
                    <Input type="date" value={editData.dst_from || ''} onChange={(e) => setEditData({ ...editData, dst_from: e.target.value })} data-testid="edit-user-dst-from" />
                    <p className="text-[11px] text-slate-500 mt-1">Punches before this date keep the old clock-change rules, so past payroll reports don't shift.</p>
                  </div>
                )}
                <p className="text-xs text-gray-500">Use this when the worker's province or state stopped changing clocks (e.g. Alberta, BC, Saskatchewan) but the company default still does. The company-wide setting lives in Settings → Time Zone &amp; Daylight Saving.</p>
              </div>


              {/* Team Assignment */}
              <div>
                <Label>Team Assignment</Label>
                <Select 
                  value={editData.team_id || 'none'} 
                  onValueChange={(value) => setEditData({...editData, team_id: value === 'none' ? '' : value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select team or leave blank" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="flex items-center space-x-2">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span>No Team (Individual)</span>
                      </span>
                    </SelectItem>
                    {teams.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        <span className="flex items-center space-x-2">
                          <Users className="w-4 h-4 text-[#A78BFA]" />
                          <span>{team.name} - {team.location}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Assign user to a team or leave as individual worker. Admins and supervisors can be team members too.
                </p>
              </div>


              <div className="flex space-x-3 pt-4">
                <Button type="submit" disabled={isLoading} className="flex-1" data-testid="edit-user-submit-btn">
                  {isLoading ? 'Updating...' : 'Update User'}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowEditDialog(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Overtime Policy Dialog */}
        <Dialog open={showBulkOtDialog} onOpenChange={(open) => { if (!bulkOtSaving) setShowBulkOtDialog(open); }}>
          <DialogContent data-testid="bulk-ot-dialog">
            <DialogHeader>
              <DialogTitle>Set overtime policy for {selectedIds.size} staff</DialogTitle>
              <DialogDescription>
                Applies the same overtime policy to every selected staff member. Payroll Workbench and QuickBooks pushes follow this setting immediately.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Policy</Label>
                <Select
                  value={bulkOt.overtime_policy}
                  onValueChange={(value) => setBulkOt({ ...bulkOt, overtime_policy: value })}
                >
                  <SelectTrigger data-testid="bulk-ot-policy-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard — overtime past weekly threshold</SelectItem>
                    <SelectItem value="averaging">Averaging agreement — averaged over the pay period</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs">Weekly OT threshold (hours)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="120"
                    step="0.5"
                    value={bulkOt.overtime_weekly_threshold}
                    onChange={(e) => setBulkOt({ ...bulkOt, overtime_weekly_threshold: e.target.value })}
                    onBlur={(e) => setBulkOt({ ...bulkOt, overtime_weekly_threshold: clampOtThreshold(e.target.value) })}
                    data-testid="bulk-ot-threshold-input"
                  />
                </div>
                {bulkOt.overtime_policy === 'averaging' && (
                  <div className="flex-1">
                    <Label className="text-xs">Agreement signed date</Label>
                    <Input
                      type="date"
                      value={bulkOt.averaging_agreement_date}
                      onChange={(e) => setBulkOt({ ...bulkOt, averaging_agreement_date: e.target.value })}
                      data-testid="bulk-ot-agreement-date-input"
                    />
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-400">
                {bulkOt.overtime_policy === 'averaging'
                  ? `Overtime only applies past ${bulkOt.overtime_weekly_threshold}h/week averaged over the pay period (e.g. ${bulkOt.overtime_weekly_threshold * 2}h on a bi-weekly period). Leave the signed date blank only if agreements are still being collected — workers without a date are flagged in payroll.`
                  : `Overtime applies past ${bulkOt.overtime_weekly_threshold}h in a single week. Any previously stored agreement date is cleared.`}
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowBulkOtDialog(false)} disabled={bulkOtSaving} data-testid="bulk-ot-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={handleBulkOtPolicy}
                disabled={bulkOtSaving || selectedIds.size === 0}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="bulk-ot-confirm-btn"
              >
                {bulkOtSaving ? 'Applying…' : `Apply to ${selectedIds.size} staff`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Archive Confirmation Dialog */}
        <Dialog open={showBulkArchiveDialog} onOpenChange={(open) => { if (!bulkArchiving) setShowBulkArchiveDialog(open); }}>
          <DialogContent data-testid="bulk-archive-dialog">
            <DialogHeader>
              <DialogTitle>Archive {selectedIds.size} user{selectedIds.size !== 1 ? 's' : ''}?</DialogTitle>
              <DialogDescription>
                Archived users will be moved to the archive (retained for 7 years) and can be restored from the Archive view.
                Super-users and yourself will be skipped automatically.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Label htmlFor="bulk-archive-reason">Archive reason (optional)</Label>
              <Input
                id="bulk-archive-reason"
                placeholder='e.g. "Q1 layoffs", "Contract ended"'
                value={bulkArchiveReason}
                onChange={(e) => setBulkArchiveReason(e.target.value)}
                data-testid="bulk-archive-reason-input"
              />
              <p className="text-xs text-slate-400">This reason is recorded against every user in this batch.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowBulkArchiveDialog(false)}
                disabled={bulkArchiving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkArchive}
                disabled={bulkArchiving || selectedIds.size === 0}
                className="bg-[#EF4444] hover:bg-red-700 text-white"
                data-testid="bulk-archive-confirm-btn"
              >
                {bulkArchiving ? 'Archiving…' : `Archive ${selectedIds.size} user${selectedIds.size !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <TimezoneAuditDialog open={showTzAudit} onOpenChange={setShowTzAudit} token={token} onFixed={fetchUsers} />
      </div>
    </div>
  );
};

export default ManageUsers;
